import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormControlLabel,
    Checkbox,
    Typography
} from '@mui/material';
import { useTranslation } from "react-i18next";
import "../../css/main.css";
import "../../css/components/Modals/UnarchiveTask.css";

export default function UnarchiveTaskModal({
    openUnarchiveTaskModal,
    handleUnarchiveTaskModalClose,
    selectedTask,
    selectedList,
    avoidMisfire,
    availableLists,
    handleUnarchiveTask
}) {
    const { t } = useTranslation();

    const [selectedListId, setSelectedListId] = useState('');
    const [initialListId, setInitialListId] = useState(''); // Store the initial list ID
    const [listWarning, setListWarning] = useState('');
    const [retainPosition, setRetainPosition] = useState(true);
    const [newPosition, setNewPosition] = useState('');
    const [showListChangeWarning, setShowListChangeWarning] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Filter the available lists to include only those that are not soft deleted
    const filteredLists = availableLists?.filter(list => !list.is_deleted);

    // Initialize selected list on modal open and prevent re-initialization
    useEffect(() => {
        if (openUnarchiveTaskModal && !isInitialized) {
            if (selectedList && selectedList.id && filteredLists?.some(list => list.id === selectedList.id)) {
                setSelectedListId(selectedList.id);
                setInitialListId(selectedList.id); // Set initial list ID
                setListWarning('');
            } else if (filteredLists?.length > 0) {
                setSelectedListId(filteredLists[0].id);
                setInitialListId(filteredLists[0].id); // Set initial list ID if no selected list
                setListWarning(t("modal.list_not_available"));
            }
            setShowListChangeWarning(false); // Reset warning when modal opens
            setIsInitialized(true); // Mark as initialized to prevent further resetting
        }
    }, [openUnarchiveTaskModal, isInitialized, selectedList, filteredLists, t]);

    // Reset isInitialized when the modal closes
    useEffect(() => {
        if (!openUnarchiveTaskModal) {
            setIsInitialized(false); // Allow re-initialization when modal reopens
        }
    }, [openUnarchiveTaskModal]);

    const handleListChange = (event) => {
        if (retainPosition) {
            setShowListChangeWarning(true); // Show warning if retaining position
        } else {
            setSelectedListId(event.target.value); // Allow change if not retaining position
            setShowListChangeWarning(false); // Clear any previous warning
        }
    };

    const handleRetainPositionChange = (event) => {
        const isChecked = event.target.checked;
        setRetainPosition(isChecked);
        if (isChecked) {
            setSelectedListId(initialListId); // Reset to initial list if retaining position
            setNewPosition(''); // Clear newPosition if retaining position
        } else {
            setShowListChangeWarning(false); // Clear warning when unchecking retain position
            setNewPosition(1)
        }
    };

    const handleNewPositionChange = (event) => {
        if (!retainPosition) { // Only allow changes if retainPosition is unchecked
            const selectedList = filteredLists.find(list => list.id === selectedListId);
            const maxPosition = selectedList ? selectedList.unarchived_task_count + 1 : 1;
            const inputValue = Number(event.target.value);
            const value = Math.max(1, Math.min(inputValue, maxPosition));
            setNewPosition(value);
        }
    };


    const handleUnarchive = async () => {
        handleUnarchiveTask(selectedTask.id, selectedListId, retainPosition, retainPosition ? null : newPosition);
        handleUnarchiveTaskModalClose();
    };

    return (
        <Dialog open={openUnarchiveTaskModal} onClose={handleUnarchiveTaskModalClose} className="modal-background-color">
            <DialogTitle>{t("modal.task_unarchive_title")}</DialogTitle>
            <DialogContent className="white-txt-color">
                {listWarning && (
                    <Typography color="error" gutterBottom>
                        {listWarning}
                    </Typography>
                )}
                {showListChangeWarning && (
                    <Typography color="error" gutterBottom>
                        {t("modal.cannot_change_list_retaining")}
                    </Typography>
                )}
                <FormControl fullWidth margin="normal">
                    <InputLabel className="white-txt-color">{t("modal.list")}</InputLabel>
                    <Select
                        name="list-select"
                        value={selectedListId || ''}
                        onChange={handleListChange}
                        className="unarchive-task-list-select"
                        MenuProps={{
                            PaperProps: {
                                className: "unarchive-task-menu-props"
                            }
                        }}
                    >
                        {filteredLists?.map(list => (
                            <MenuItem key={list.id} value={list.id}>
                                {list.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={retainPosition}
                            onChange={handleRetainPositionChange}
                            color="primary"
                        />
                    }
                    label={t("modal.retain_position")}
                />
                {!retainPosition && (
                    <>
                        <TextField
                            margin="normal"
                            label={t("modal.new_position")}
                            type="number"
                            fullWidth
                            value={newPosition || 1}
                            onChange={handleNewPositionChange}
                            InputLabelProps={{ className: "white-txt-color" }}
                            InputProps={{ className: "white-txt-color" }}
                        />
                        <Typography>
                            {t("modal.list_task_total", {tasks : selectedList ? selectedList.unarchived_task_count + 1 : 1})}
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleUnarchiveTaskModalClose} className="button-dark-bg">
                    {t("global.cancel")}
                </Button>
                <Button onClick={handleUnarchive} disabled={avoidMisfire || (!retainPosition && (newPosition === ''))} className="button-dark-bg"
                >
                    {t("modal.unarchive")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
